// entityTypes.ts — entity-type helpers for the v4 drill-through.
//
// The PWA data API (`/api/pwa/entities/:type/:id`) keys entities by a path-form
// type (plural). The design/graph refers to entities by singular type or by ID
// prefix. These helpers normalise both into the path form the API expects, so a
// linkPill carrying any of ("step" | "steps" | "STEP-123") resolves correctly.

export interface TypeMeta {
  /** Path form used by the data API. */
  path: string;
  /** Short human label for sheet eyebrows. */
  label: string;
}

export const TYPE_META: Record<string, TypeMeta> = {
  steps: { path: "steps", label: "Step" },
  milestones: { path: "milestones", label: "Milestone" },
  tracks: { path: "tracks", label: "Track" },
  vlrs: { path: "vlrs", label: "VLR" },
  decisions: { path: "decisions", label: "Decision" },
  processes: { path: "processes", label: "Process" },
  spikes: { path: "spikes", label: "Spike" },
  "milestone-reviews": { path: "milestone-reviews", label: "Review" },
};

const SINGULAR_TO_PATH: Record<string, string> = {
  step: "steps",
  milestone: "milestones",
  track: "tracks",
  vlr: "vlrs",
  decision: "decisions",
  principle: "decisions",
  process: "processes",
  spike: "spikes",
  "milestone-review": "milestone-reviews",
  milestone_review: "milestone-reviews",
};

/** Normalise any type spelling (singular/plural/underscore) to the API path form. */
export function normalizeType(type: string): string | null {
  if (!type) return null;
  const t = type.toLowerCase();
  if (TYPE_META[t]) return t;
  if (SINGULAR_TO_PATH[t]) return SINGULAR_TO_PATH[t];
  return null;
}

/** Infer the path-form entity type from an entity ID (e.g. "STEP-123" → "steps"). */
export function pathTypeFromId(id: string): string | null {
  if (/^STEP-\d+$/i.test(id)) return "steps";
  if (/^VLR-\d+$/i.test(id)) return "vlrs";
  if (/^DEC-\d+$/i.test(id)) return "decisions";
  if (/^SPIKE-/i.test(id)) return "spikes";
  if (/^TRACK-/i.test(id)) return "tracks";
  if (/^PROC-/i.test(id)) return "processes";
  if (/^MR-/i.test(id)) return "milestone-reviews";
  if (/^M-[A-Z0-9]+$/i.test(id)) return "milestones";
  return null;
}

export function typeLabel(type: string): string {
  const norm = normalizeType(type);
  return (norm && TYPE_META[norm]?.label) ?? type;
}
